<template>
  <div class="login max-w-md mx-auto">
    <h1 class="text-3xl font-bold mb-8 text-primary dark:text-primary-light">
      {{ resetToken ? 'Reset password' : 'Forgot password' }}
    </h1>

    <!-- Request flow -->
    <div v-if="!resetToken && !sent" class="card p-8">
      <p class="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
        Enter your email and we'll send you a password reset link. The link expires after 1 hour.
      </p>
      <div class="mb-6">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="fp-email">Email</label>
        <input id="fp-email" v-model="email" type="email" class="input w-full"
          :disabled="loading" placeholder="Enter your email address" />
      </div>
      <button class="btn-primary w-full" :disabled="loading || !email" @click="requestReset">
        {{ loading ? 'Sending...' : 'Send reset link' }}
      </button>
      <div class="text-center mt-4">
        <router-link to="/login" class="text-primary dark:text-primary-light hover:underline text-sm">
          Back to login
        </router-link>
      </div>
    </div>

    <!-- Request-sent confirmation -->
    <div v-else-if="!resetToken && sent" class="card p-8 text-center">
      <h3 class="text-lg font-semibold mb-2 text-text-primary dark:text-text-primary-dark">Check your email</h3>
      <p class="text-text-secondary dark:text-text-secondary-dark mb-4">
        If an account with <strong>{{ email }}</strong> exists, a reset link has been sent.
      </p>
      <router-link to="/login" class="text-primary dark:text-primary-light hover:underline text-sm">
        Back to login
      </router-link>
    </div>

    <!-- Reset (confirm) flow -->
    <div v-else-if="resetToken && !done" class="card p-8">
      <p class="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
        Choose a new password for your account. Minimum 10 characters.
      </p>
      <div class="mb-4">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="new-pw">New password</label>
        <input id="new-pw" v-model="newPassword" type="password" autocomplete="new-password" class="input w-full"
          :disabled="loading" minlength="10" />
      </div>
      <div class="mb-6">
        <label class="block text-text-secondary dark:text-text-secondary-dark text-sm font-bold mb-2" for="new-pw2">Confirm password</label>
        <input id="new-pw2" v-model="confirmPassword" type="password" autocomplete="new-password" class="input w-full"
          :disabled="loading" minlength="10" />
      </div>
      <p v-if="error" class="text-red-500 text-sm mb-4">{{ error }}</p>
      <button class="btn-primary w-full"
        :disabled="loading || newPassword.length < 10 || newPassword !== confirmPassword"
        @click="confirmReset">
        {{ loading ? 'Resetting...' : 'Reset password' }}
      </button>
    </div>

    <!-- Done -->
    <div v-else class="card p-8 text-center">
      <h3 class="text-lg font-semibold mb-2 text-text-primary dark:text-text-primary-dark">Password reset</h3>
      <p class="text-text-secondary dark:text-text-secondary-dark mb-4">
        You can now sign in with your new password.
      </p>
      <router-link to="/login" class="btn-primary inline-block">Go to login</router-link>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'

const getApiBase = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}

export default {
  name: 'ForgotPasswordView',
  setup() {
    const route = useRoute()
    const resetToken = computed(() => route.params.token || '')

    const email = ref('')
    const newPassword = ref('')
    const confirmPassword = ref('')
    const loading = ref(false)
    const sent = ref(false)
    const done = ref(false)
    const error = ref('')

    const requestReset = async () => {
      loading.value = true
      error.value = ''
      try {
        await axios.post(`${getApiBase()}/password/reset/request`, { email: email.value })
        sent.value = true
      } catch {
        // Intentionally silent — server returns a generic message so we do too.
        sent.value = true
      } finally {
        loading.value = false
      }
    }

    const confirmReset = async () => {
      if (newPassword.value !== confirmPassword.value) {
        error.value = 'Passwords do not match'
        return
      }
      loading.value = true
      error.value = ''
      try {
        await axios.post(`${getApiBase()}/password/reset/confirm`, {
          token: resetToken.value,
          new_password: newPassword.value
        })
        done.value = true
      } catch (e) {
        error.value = e.response?.data?.error || 'Failed to reset password. The link may have expired.'
      } finally {
        loading.value = false
      }
    }

    return { resetToken, email, newPassword, confirmPassword, loading, sent, done, error, requestReset, confirmReset }
  }
}
</script>
